import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { rem, rgba } from 'polished';
import screen from 'superior-mq';
import { bp, hover } from 'Styles/helpers';
import objectPath from 'object-path';
import Container from '../container';
import Section from '../section';
import TextWave from '../text-wave';
import HomeH3 from '../home/HomeH3';
import LazyImg from '../lazy-img';
import StaggerIn from '../stagger-in';
import Waves from '../waves';
import InlineLink from '../inline-link';

const imgStyles = `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

const sectionStyles = css`
  position: relative;
  background-color: transparent;

  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 50%;
    z-index: 0;
    background: linear-gradient(0deg, ${rgba('#000', 0.8)}, ${rgba('#000', 0)});
  }
`;

const colorNumbers = (str) => {
  const [number, ...rest] = str.split(' ');

  if (!/\d.+/.test(number)) return str;

  return (
    <React.Fragment>
      <span>{number}</span> {rest.join(' ')}
    </React.Fragment>
  );
};

const StatLink = ({ link, children }) => (
  link
    ?
      <LinkStat href={link}>
        {children}
      </LinkStat>
    :
      <p>
        {children}
      </p>
);

const Impact = ({ data, id }) => {
  if (!data) return null;

  return (
    <Section dark styles={sectionStyles} id={id}>
      <Waves position="bottom" inverted alignment="bottom" height={200} color="#fff" />
      <Container>

        <TextWave>
          <HomeH3>{data.fields.statGridTitle}</HomeH3>
        </TextWave>
        <StaggerIn>
          <FactGrid>
            <Fact row="1" col="2" className="js-stagger">
              <StatLink link={data.fields.stat1Link}>
                {colorNumbers(data.fields.stat1)}
              </StatLink>
            </Fact>

            <Fact row="10" col="3" className="js-stagger">
              <StatLink link={data.fields.stat3Link}>
                {colorNumbers(data.fields.stat3)}
              </StatLink>
            </Fact>

            <Fact row="6" col="12" className="js-stagger">
              <StatLink link={data.fields.stat2Link}>
                {colorNumbers(data.fields.stat2)}
              </StatLink>
            </Fact>

            <Fact row="15" col="10" className="js-stagger">
              <StatLink link={data.fields.stat4Link}>
                {colorNumbers(data.fields.stat4)}
              </StatLink>
            </Fact>
          </FactGrid>
        </StaggerIn>

      </Container>

      {objectPath.has(data, 'fields.statGridBackgroundImage.fields.file.url') &&
        <LazyImg
          width={1800}
          height={900}
          src={`${data.fields.statGridBackgroundImage.fields.file.url}?w=1800&h=900&q=90&fm=jpg&fl=progressive`}
          styles={imgStyles}
        />
      }

    </Section>
  );
};

const FactGrid = styled.div`
  --bg-color: ${props => props.theme.black};
  position: relative;
  display: grid;
  grid-template-rows: repeat(17, 22px);
  grid-template-columns: repeat(18, 1fr);
  margin: 60px auto 0;
  max-width: 1076px;
  z-index: 1;

  ${screen.below(bp.portrait, `
    grid-template-columns: auto;
    grid-template-rows: repeat(4, auto);
    grid-row-gap: 30px;
    place-self: center;
  `)}
`;

const LinkStat = styled(InlineLink)`
  color: inherit;
  text-decoration: none;
  transition: background-color .4s ease-in;

  ${props => hover(`
    --bg-color: ${props.theme.blue};

    span { color: #fff; }
  `)}
`;

const Fact = styled.div`
  grid-column: ${props => props.col} / span 8;
  grid-row: ${props => props.row} / span 3;

  ${css`
    ${props => screen.below(bp.tablet, `
      grid-column: ${props.col} / span 10;
    `)}
  `}

  ${screen.below(bp.portrait, `
    grid-column: 1;
    grid-row: auto;
    justify-self: center;
  `)}

  p,
  ${LinkStat} {
    display: inline-block;
    padding: 17px 40px 22px;
    margin: 0;
    line-height: 1;
    font-size: ${rem(27)};
    background-color: var(--bg-color);

    ${screen.below(bp.laptopSm, `
      font-size: ${rem(22)};
    `)}

    ${screen.below(bp.mobile, `
      font-size: 1rem;
      padding: 14px 30px 16px;
    `)}

    ${screen.below(bp.mobileRealSm, `
      padding: 14px 20px 16px;
    `)}
  }

  span {
    color: ${props => props.theme.blue};
    font-weight: 600;
    transition: color .4s ease-in;
  }
`;

Impact.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
};

export default Impact;
